import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../api/client';
import { useFamilyStore } from './familyStore';

export interface User {
  id: string;
  email: string | null;
  username: string;
  first_name: string;
  last_name: string | null;
  avatar_url: string | null;
  date_of_birth: string | null;
  is_active: boolean;
  is_verified: boolean;
  is_minor: boolean;
  can_access_app: boolean;
  is_superadmin?: boolean;
  last_family_id?: string | null;
  google_id?: string | null;
  notifications_enabled?: boolean;
  created_at: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  login: (username: string, password: string) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  setUser: (user: User) => void;
  checkAuth: () => Promise<void>;
  linkGoogleAccount: (idToken: string, password: string) => Promise<void>;
  unlinkGoogleAccount: () => Promise<void>;
}

interface RegisterData {
  email: string;
  username: string;
  password: string;
  first_name: string;
  last_name?: string;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,

      login: async (username: string, password: string) => {
        // Clear any stale family selection before switching accounts
        localStorage.removeItem('family-storage');
        useFamilyStore.getState().setCurrentFamily(null);

        const response = await api.post('/auth/login', { username, password });
        const { access_token, refresh_token, user } = response.data;
        
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        
        set({ user, isAuthenticated: true, isLoading: false });
        
        // Auto-select family after login
        useFamilyStore.getState().autoSelectFamily(user.last_family_id);
      },

      loginWithGoogle: async (idToken: string) => {
        // Clear any stale family selection before switching accounts
        localStorage.removeItem('family-storage');
        useFamilyStore.getState().setCurrentFamily(null);

        const response = await api.post('/auth/google', { id_token: idToken });
        const { access_token, refresh_token, user } = response.data;
        
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        
        set({ user, isAuthenticated: true, isLoading: false });
        
        // Auto-select family after login
        useFamilyStore.getState().autoSelectFamily(user.last_family_id);
      },

      register: async (data: RegisterData) => {
        // Clear any stale family selection before switching accounts
        localStorage.removeItem('family-storage');
        useFamilyStore.getState().setCurrentFamily(null);

        const response = await api.post('/auth/register', data);
        const { access_token, refresh_token, user } = response.data;
        
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        
        set({ user, isAuthenticated: true, isLoading: false });
      },

      logout: () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('family-storage');
        useFamilyStore.getState().setCurrentFamily(null);
        set({ user: null, isAuthenticated: false, isLoading: false });
      },

      setUser: (user: User) => {
        set({ user, isAuthenticated: true, isLoading: false });
      },

      checkAuth: async () => {
        const token = localStorage.getItem('access_token');
        if (!token) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }

        try {
          const response = await api.get('/auth/me');
          const user = response.data;
          set({ user, isAuthenticated: true, isLoading: false });
          
          // Auto-select family on auth check (page refresh)
          useFamilyStore.getState().autoSelectFamily(user.last_family_id);
        } catch (error: any) {
          // Always set isLoading to false, even on error
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          set({ user: null, isAuthenticated: false, isLoading: false });
          
          // Log error for debugging (but don't throw)
          if (error.response) {
            console.warn('[Auth] Auth check failed:', error.response.status, error.response.data);
          } else if (error.request) {
            console.error('[Auth] Network error during auth check:', error.message);
          } else {
            console.error('[Auth] Auth check error:', error.message);
          }
        }
      },
      
      linkGoogleAccount: async (idToken: string, password: string) => {
        const response = await api.post('/auth/link-google', {
          id_token: idToken,
          password: password,
        });
        set({ user: response.data, isAuthenticated: true });
      },
      
      unlinkGoogleAccount: async () => {
        const response = await api.post('/auth/unlink-google');
        set({ user: response.data, isAuthenticated: true });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);

